/**
 * GamelessToggleButton toggles "gameless" mode: hiding the game board so
 * video can use the full screen. Any mate can toggle it and it applies to
 * every connected client, without affecting the active game's state.
 *
 * When to use: Rendered by Header, always available (independent of whether
 * a game is active).
 */
import * as React from 'react'
import { cn } from '@merels/ui'
import { IconButton } from '../../library/IconButton/IconButton'
import { VideoGridIcon } from '../../library/Icons/VideoGridIcon'
import { BoardViewIcon } from '../../library/Icons/BoardViewIcon'
import { headerTestIds } from './header.testIds'

export interface GamelessToggleButtonProps {
  /** Whether the room is currently in gameless mode */
  isGameless: boolean
  /** Callback to toggle gameless mode */
  onToggle: () => void
  /** Ref for focus management (callback or object ref) */
  buttonRef?: React.Ref<HTMLButtonElement>
  /** Tab index for focus navigation */
  tabIndex?: number
  /** Whether this button is currently focused */
  isFocused?: boolean
  /** Accent color for styling */
  accentColor?: string
  /** Ink when filled with accentColor (hover) */
  accentInkColor?: string
  /** Background color for focus ring */
  backgroundColor?: string
}

export function GamelessToggleButton({
  isGameless,
  onToggle,
  buttonRef,
  tabIndex,
  isFocused,
  accentColor,
  accentInkColor = '#ffffff',
  backgroundColor,
}: GamelessToggleButtonProps) {
  const label = isGameless ? 'Show game' : 'Hide game'

  return (
    <IconButton
      ref={buttonRef}
      data-testid={headerTestIds.gamelessToggleButton}
      onClick={onToggle}
      aria-label={label}
      title={label}
      aria-pressed={isGameless}
      tabIndex={tabIndex}
      className={cn(
        "flex h-[var(--menu-btn-size)] w-[var(--menu-btn-size)] items-center justify-center rounded-lg transition-colors",
        isFocused && "outline-none"
      )}
      style={{
        ...(accentColor ? { color: accentColor } : {}),
        ...(isGameless && accentColor ? { backgroundColor: accentColor, color: accentInkColor } : {}),
        ...(isFocused && accentColor ? {
          boxShadow: `0 0 0 2px ${accentColor}, 0 0 0 4px ${backgroundColor}`,
        } : {}),
      }}
      onMouseEnter={(e) => {
        if (accentColor && !isGameless) {
          e.currentTarget.style.backgroundColor = accentColor
          e.currentTarget.style.color = accentInkColor
        }
      }}
      onMouseLeave={(e) => {
        if (accentColor && !isGameless) {
          e.currentTarget.style.backgroundColor = ''
          e.currentTarget.style.color = accentColor
        }
      }}
      icon={
        isGameless ? (
          <BoardViewIcon width="var(--menu-icon-size)" height="var(--menu-icon-size)" />
        ) : (
          <VideoGridIcon width="var(--menu-icon-size)" height="var(--menu-icon-size)" />
        )
      }
    />
  )
}
